/**
 * Safe formula evaluator for pricing calculations
 * Supports basic math operations and ternary operators
 */

export interface FormulaContext {
  [key: string]: any;
}

export interface ImageDimensions {
  width: number;
  height: number;
}

export interface CustomParametersInput {
  inputImageDimensions?: ImageDimensions[];
  targetImageDimensions?: ImageDimensions | null;
}

/**
 * Variables owned by FormulaEvaluator. Callers and derived widget contexts must
 * not override these values because they are calculated from trusted execution
 * inputs immediately before formula evaluation.
 */
export const FORMULA_EVALUATOR_GENERATED_VARIABLES: ReadonlySet<string> = new Set([
  'input_img_mpx',
  'input_img_count',
  'input_img_count_may_zero',
  'target_img_mpx',
  'target_img_width',
  'target_img_height',
  'target_long_side',
  'target_short_side',
  'target_aspect_ratio',
  'prompt_length_1k',
  'prompt_length_1k_may_zero',
  'prompt_length_ceil_100',
  'prompt_length_ceil_100_may_zero',
  'text_length_ceil_100',
  'text_length_ceil_100_may_zero',
  'has_prompt',
  'has_audio',
  'has_video',
  'audio_duration_may_zero',
  'video_duration_may_zero',
  'audio_duration_exact_may_zero',
  'video_duration_exact_may_zero',
]);

/**
 * Optional browser-probed values that may be forwarded to providers but are
 * not trustworthy enough to affect billing. Keep this runtime prohibition in
 * addition to admin documentation so imported or stale formulas fail closed.
 */
export const NON_BILLING_FORMULA_VARIABLES = [
  'video_fps',
  'video_frame_count',
  'video_size_bytes',
] as const;

function assertNoNonBillingVariables(formula: string): void {
  const formulaWithoutStrings = formula.replace(/"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/gu, '');
  const identifiers: string[] = formulaWithoutStrings.match(/[A-Za-z_][A-Za-z0-9_]*/gu) ?? [];
  const forbidden = NON_BILLING_FORMULA_VARIABLES.find(variable => (
    identifiers.includes(variable)
  ));
  if (forbidden) {
    throw new Error(`${forbidden} is informational and cannot be used for pricing`);
  }
}

export class FormulaEvaluator {
  /**
   * Calculate custom parameters from input data
   */
  static calculateCustomParameters(customInput?: CustomParametersInput, context?: FormulaContext): Record<string, number> {
    const result: Record<string, number> = {};

    // Calculate input_img_mpx - maximum megapixels from the largest input image
    if (customInput?.inputImageDimensions && customInput.inputImageDimensions.length > 0) {
      const maxMpx = Math.max(...customInput.inputImageDimensions.map(dim =>
        (dim.width * dim.height) / 1_000_000
      ));
      result.input_img_mpx = maxMpx;
    } else {
      result.input_img_mpx = 0;
    }

    // Calculate input_img_count - number of input/reference images
    // (e.g. Seedream prices per reference image)
    const inputImageCount = customInput?.inputImageDimensions?.length ?? 0;
    result.input_img_count = inputImageCount;

    // Calculate input_img_count_may_zero - same as input_img_count, signals that 0 is acceptable
    result.input_img_count_may_zero = inputImageCount;

    // Calculate target_img_mpx - target image megapixels
    // Plus target dimension variables for formulas that price by long/short side or aspect ratio
    // (e.g. gpt-image-2 tiers by long side, not total megapixels)
    if (customInput?.targetImageDimensions) {
      // Clamp negative width/height to 0 to avoid negative tokens — a negative value
      // always indicates a bug upstream (dimensions come from extracted image metadata,
      // not untrusted input). In the degenerate zero-side case, aspect_ratio falls back
      // to 0 via the guard below.
      const width = Math.max(0, customInput.targetImageDimensions.width);
      const height = Math.max(0, customInput.targetImageDimensions.height);
      result.target_img_mpx = (width * height) / 1_000_000;
      result.target_img_width = width;
      result.target_img_height = height;
      result.target_long_side = Math.max(width, height);
      result.target_short_side = Math.min(width, height);
      // Guard against division by zero (only triggers on degenerate dimensions)
      result.target_aspect_ratio = result.target_short_side === 0
        ? 0
        : result.target_long_side / result.target_short_side;
    } else {
      result.target_img_mpx = 0;
      result.target_img_width = 0;
      result.target_img_height = 0;
      result.target_long_side = 0;
      result.target_short_side = 0;
      result.target_aspect_ratio = 0;
    }

    // Calculate prompt_length_1k - prompt length in thousands of characters
    const prompt = context?.prompt || context?.prompt_text || '';
    const promptLength = typeof prompt === 'string' ? prompt.length : 0;
    result.prompt_length_1k = promptLength / 1000;

    // Calculate prompt_length_1k_may_zero - same as prompt_length_1k, signals that 0 is acceptable
    result.prompt_length_1k_may_zero = promptLength / 1000;

    // Calculate prompt_length_ceil_100 - prompt length in hundreds of characters (stepped: Math.ceil)
    result.prompt_length_ceil_100 = Math.ceil(promptLength / 100);

    // Calculate prompt_length_ceil_100_may_zero - same as prompt_length_ceil_100, signals that 0 is acceptable
    result.prompt_length_ceil_100_may_zero = Math.ceil(promptLength / 100);

    // Calculate text_length_ceil_100 - text field length in hundreds of characters (stepped: Math.ceil)
    const text = context?.text || '';
    const textLength = typeof text === 'string' ? text.length : 0;
    result.text_length_ceil_100 = Math.ceil(textLength / 100);

    // Calculate text_length_ceil_100_may_zero - same as text_length_ceil_100, signals that 0 is acceptable
    result.text_length_ceil_100_may_zero = Math.ceil(textLength / 100);

    // Calculate has_prompt - 1 if prompt is provided, 0 if empty
    result.has_prompt = promptLength > 0 ? 1 : 0;

    return result;
  }

  /**
   * Evaluate pricing formula with given parameters
   * Returns calculated cost or throws error if formula is invalid
   */
  static evaluate(
    formula: string,
    context: FormulaContext,
    customInput?: CustomParametersInput
  ): number {
    if (!formula || formula.trim() === '') {
      throw new Error('Formula is empty');
    }

    try {
      assertNoNonBillingVariables(formula);

      // Calculate custom parameters and merge with context
      const customParams = this.calculateCustomParameters(customInput, context);
      const enrichedContext = { ...context, ...customParams };

      // Inject optional media duration variables
      enrichedContext.has_audio = enrichedContext.audio_duration ? 1 : 0;
      enrichedContext.has_video = enrichedContext.video_duration ? 1 : 0;
      enrichedContext.audio_duration_may_zero = enrichedContext.audio_duration || 0;
      enrichedContext.video_duration_may_zero = enrichedContext.video_duration || 0;

      for (const type of ['audio', 'video'] as const) {
        const exactKey = `${type}_duration_exact`;
        const roundedDuration = enrichedContext[`${type}_duration`];
        // Older callers only send rounded durations. Preserve their billing inputs.
        if (enrichedContext[exactKey] == null && typeof roundedDuration === 'number' && Number.isFinite(roundedDuration)) {
          enrichedContext[exactKey] = roundedDuration;
        }
        enrichedContext[`${exactKey}_may_zero`] = enrichedContext[exactKey] || 0;
      }

      // Create safe evaluation context
      const safeContext = this.createSafeContext(enrichedContext);

      // Replace parameter names with their values
      let processedFormula = this.substituteParameters(formula, safeContext);

      // Normalize whitespace so multiline formulas (with \n, \r, \t between
      // tokens) parse the same as single-line. The ternary regex in
      // evaluateExpression uses `.+?` which doesn't cross newlines.
      processedFormula = processedFormula.replace(/\s+/g, ' ').trim();

      // Validate formula (security check)
      this.validateFormula(processedFormula);

      // Evaluate using custom expression parser (no dynamic code generation)
      const result = this.safeEvaluate(processedFormula);

      // Ensure result is a valid number
      if (typeof result !== 'number' || isNaN(result) || !isFinite(result)) {
        throw new Error(`Formula returned invalid result: ${result}`);
      }

      // Ensure non-negative (allow zero, but not negative)
      if (result < 0) {
        throw new Error(`Formula returned negative result: ${result}`);
      }

      // Round up to integer (tokens must be whole numbers)
      return Math.ceil(result);

    } catch (error: any) {
      console.error('❌ Formula evaluation failed:', {
        formula,
        contextKeys: Object.keys(context),
        error: error.message
      });
      throw new Error(`Invalid pricing formula: ${error.message}`);
    }
  }

  /**
   * Create safe context with default values and type conversion
   */
  private static createSafeContext(context: FormulaContext): FormulaContext {
    const safeContext: FormulaContext = {};

    for (const [key, value] of Object.entries(context)) {
      // Convert to appropriate type
      if (typeof value === 'boolean') {
        safeContext[key] = value;
      } else if (typeof value === 'number') {
        safeContext[key] = value;
      } else if (typeof value === 'string') {
        // Escape quotes and wrap in quotes for string comparison
        safeContext[key] = `"${value.replace(/"/g, '\\"')}"`;
      } else {
        // Skip objects/arrays for security (silently)
      }
    }

    return safeContext;
  }

  /**
   * Substitute parameter names with their values
   */
  private static substituteParameters(
    formula: string,
    context: FormulaContext
  ): string {
    let result = formula;

    // Sort keys by length (longest first) to avoid partial replacements
    // Example: 'audio_enabled' should be replaced before 'audio'
    const sortedKeys = Object.keys(context).sort((a, b) => b.length - a.length);

    for (const key of sortedKeys) {
      const value = context[key];
      // Use word boundary regex to avoid partial replacements
      // \b matches word boundary (between \w and \W)
      const regex = new RegExp(`\\b${this.escapeRegex(key)}\\b`, 'g');
      result = result.replace(regex, String(value));
    }

    return result;
  }

  /**
   * Escape special regex characters in parameter names
   */
  private static escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Validate formula for security (whitelist approach)
   */
  private static validateFormula(formula: string): void {
    // Check for dangerous patterns first
    const dangerousPatterns = [
      /\beval\b/i,
      /\bFunction\b/i,
      /\bimport\b/i,
      /\brequire\b/i,
      /\bprocess\b/i,
      /\b__proto__\b/i,
      /\bconstructor\b/i,
      /<script/i,
      /javascript:/i,
      /\bwindow\b/i,
      /\bglobal\b/i,
      /\bthis\b/i
    ];

    for (const pattern of dangerousPatterns) {
      if (pattern.test(formula)) {
        throw new Error(`Forbidden pattern detected: ${pattern.source}`);
      }
    }

    // Ensure only allowed characters and functions
    // Allowed: numbers, operators, Math functions, ternary operators, parentheses, quotes
    const allowedMathFunctions = [
      'Math.ceil',
      'Math.floor',
      'Math.round',
      'Math.min',
      'Math.max',
      'Math.abs'
    ];

    // Replace Math functions temporarily for validation
    let checkFormula = formula;
    for (const fn of allowedMathFunctions) {
      checkFormula = checkFormula.replace(new RegExp(this.escapeRegex(fn), 'g'), 'MATHFN');
    }

    // Check if remaining characters are safe
    // Allow: alphanumeric (for variable names and string values), whitespace, operators, quotes, and MATHFN placeholder
    const safePattern = /^[\w\s+\-*/%().,?:><=!&|"'MATHFN]+$/;
    if (!safePattern.test(checkFormula)) {
      throw new Error('Formula contains forbidden characters or functions');
    }
  }

  /**
   * Safe evaluation without Function constructor (Cloudflare Workers compatible)
   * Handles simple numeric formulas without dynamic code generation
   */
  private static safeEvaluate(expression: string): number {
    try {
      // Simple case: just a number
      const numericValue = parseFloat(expression);
      if (!isNaN(numericValue) && isFinite(numericValue) && /^\d+(\.\d+)?$/.test(expression.trim())) {
        return numericValue;
      }

      // For simple arithmetic expressions, use a basic parser
      // This is a simplified evaluator that doesn't use Function constructor
      const result = this.evaluateExpression(expression);
      return result;
    } catch (error: any) {
      throw new Error(`Evaluation error: ${error.message}`);
    }
  }

  /**
   * Find matching closing parenthesis for an opening one
   */
  private static findMatchingParen(str: string, startPos: number): number {
    let depth = 1;
    for (let i = startPos + 1; i < str.length; i++) {
      if (str[i] === '(') depth++;
      else if (str[i] === ')') {
        depth--;
        if (depth === 0) return i;
      }
    }
    return -1;
  }

  /**
   * Split function arguments by comma, respecting nested parentheses
   */
  private static splitArguments(str: string): string[] {
    const args: string[] = [];
    let depth = 0;
    let start = 0;
    for (let i = 0; i < str.length; i++) {
      if (str[i] === '(') depth++;
      else if (str[i] === ')') depth--;
      else if (str[i] === ',' && depth === 0) {
        args.push(str.substring(start, i));
        start = i + 1;
      }
    }
    args.push(str.substring(start));
    return args;
  }

  /**
   * Simple expression evaluator without using Function constructor
   * Supports: +, -, *, /, (), Math functions, ternary operator
   */
  private static evaluateExpression(expr: string): number {
    expr = expr.trim();

    // Handle Math functions with iteration safety - process from innermost to outermost
    let iterations = 0;
    const maxIterations = 100;
    while ((expr.includes('Math.') && iterations < maxIterations)) {
      const prevExpr = expr;

      // Find Math function call and extract its argument properly (handling nested parens)
      const mathFunctions = ['ceil', 'floor', 'round', 'min', 'max', 'abs'];
      let replaced = false;

      for (const fn of mathFunctions) {
        const pattern = `Math.${fn}(`;
        const idx = expr.indexOf(pattern);
        if (idx !== -1) {
          const openParen = idx + pattern.length - 1;
          const closeParen = this.findMatchingParen(expr, openParen);
          if (closeParen !== -1) {
            const inner = expr.substring(openParen + 1, closeParen);
            let result: number;

            if (fn === 'min' || fn === 'max') {
              const values = this.splitArguments(inner).map(a => this.evaluateExpression(a.trim()));
              result = fn === 'min' ? Math.min(...values) : Math.max(...values);
            } else {
              const value = this.evaluateExpression(inner);
              result = Math[fn as 'ceil' | 'floor' | 'round' | 'abs'](value);
            }

            expr = expr.substring(0, idx) + String(result) + expr.substring(closeParen + 1);
            replaced = true;
            break;
          }
        }
      }

      // Prevent infinite loop if no progress
      if (!replaced || expr === prevExpr) break;
      iterations++;
    }
    if (iterations >= maxIterations) {
      throw new Error('Too many Math function calls');
    }

    // Handle parentheses FIRST (before ternary) - process innermost first
    let parenIterations = 0;
    const maxParenIterations = 100;
    while (expr.includes('(') && parenIterations < maxParenIterations) {
      const prevExpr = expr;
      expr = expr.replace(/\(([^()]+)\)/g, (_, inner) => {
        return String(this.evaluateExpression(inner));
      });
      // Prevent infinite loop if no progress
      if (expr === prevExpr) {
        throw new Error('Unable to resolve parentheses');
      }
      parenIterations++;
    }
    if (parenIterations >= maxParenIterations) {
      throw new Error('Too many nested parentheses');
    }

    // Handle ternary operator AFTER parentheses: condition ? true_value : false_value
    const ternaryMatch = expr.match(/^(.+?)\s*\?\s*(.+?)\s*:\s*(.+)$/);
    if (ternaryMatch) {
      const [, condition, trueVal, falseVal] = ternaryMatch;
      const condResult = this.evaluateCondition(condition);
      return this.evaluateExpression(condResult ? trueVal : falseVal);
    }

    // Handle arithmetic operations (order: *, /, %, +, -)
    // Multiplication, division, and modulo - repeat until no more operations
    let mulDivModIterations = 0;
    const maxMulDivModIterations = 100;
    while ((expr.match(/(-?\d+(?:\.\d+)?)\s*\*\s*(-?\d+(?:\.\d+)?)/) || expr.match(/(-?\d+(?:\.\d+)?)\s*\/\s*(-?\d+(?:\.\d+)?)/) || expr.match(/(-?\d+(?:\.\d+)?)\s*%\s*(-?\d+(?:\.\d+)?)/)) && mulDivModIterations < maxMulDivModIterations) {
      const prevExpr = expr;
      expr = expr.replace(/(-?\d+(?:\.\d+)?)\s*\*\s*(-?\d+(?:\.\d+)?)/, (_, a, b) => {
        return String(parseFloat(a) * parseFloat(b));
      });
      expr = expr.replace(/(-?\d+(?:\.\d+)?)\s*\/\s*(-?\d+(?:\.\d+)?)/, (_, a, b) => {
        return String(parseFloat(a) / parseFloat(b));
      });
      expr = expr.replace(/(-?\d+(?:\.\d+)?)\s*%\s*(-?\d+(?:\.\d+)?)/, (_, a, b) => {
        return String(parseFloat(a) % parseFloat(b));
      });
      // Prevent infinite loop if no progress
      if (expr === prevExpr) break;
      mulDivModIterations++;
    }

    // Addition and subtraction - repeat until no more operations
    let addSubIterations = 0;
    const maxAddSubIterations = 100;
    while ((expr.match(/(-?\d+(?:\.\d+)?)\s*\+\s*(-?\d+(?:\.\d+)?)/) || expr.match(/(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)/)) && addSubIterations < maxAddSubIterations) {
      const prevExpr = expr;
      expr = expr.replace(/(-?\d+(?:\.\d+)?)\s*\+\s*(-?\d+(?:\.\d+)?)/, (_, a, b) => {
        return String(parseFloat(a) + parseFloat(b));
      });
      expr = expr.replace(/(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)/, (_, a, b) => {
        return String(parseFloat(a) - parseFloat(b));
      });
      // Prevent infinite loop if no progress
      if (expr === prevExpr) break;
      addSubIterations++;
    }

    // Final result should be a number - validate no remaining identifiers or function calls
    const trimmedExpr = expr.trim();

    // Check for remaining function calls (anything followed by parentheses)
    if (/[a-zA-Z_]\w*\s*\(/.test(trimmedExpr)) {
      throw new Error(`Cannot evaluate expression: contains function call: ${trimmedExpr}`);
    }

    // Check for remaining identifiers (should only have numbers, operators, and whitespace)
    if (/[a-zA-Z_]\w*/.test(trimmedExpr)) {
      throw new Error(`Cannot evaluate expression: contains identifier: ${trimmedExpr}`);
    }

    const result = parseFloat(trimmedExpr);
    if (isNaN(result) || !trimmedExpr.match(/^-?\d+(\.\d+)?$/)) {
      throw new Error(`Cannot evaluate expression: ${trimmedExpr}`);
    }
    return result;
  }

  /**
   * Evaluate boolean condition for ternary operator
   */
  private static evaluateCondition(condition: string): boolean {
    condition = condition.trim();

    // Handle comparison operators (check === and !== before == and !=)
    if (condition.includes('===')) {
      const [left, right] = condition.split('===').map(s => s.trim());
      return this.compareValues(left, right) === 0;
    }
    if (condition.includes('!==')) {
      const [left, right] = condition.split('!==').map(s => s.trim());
      return this.compareValues(left, right) !== 0;
    }
    if (condition.includes('==')) {
      const [left, right] = condition.split('==').map(s => s.trim());
      return this.compareValues(left, right) === 0;
    }
    if (condition.includes('!=')) {
      const [left, right] = condition.split('!=').map(s => s.trim());
      return this.compareValues(left, right) !== 0;
    }
    if (condition.includes('>=')) {
      const [left, right] = condition.split('>=').map(s => s.trim());
      return this.evaluateExpression(left) >= this.evaluateExpression(right);
    }
    if (condition.includes('<=')) {
      const [left, right] = condition.split('<=').map(s => s.trim());
      return this.evaluateExpression(left) <= this.evaluateExpression(right);
    }
    if (condition.includes('>')) {
      const [left, right] = condition.split('>').map(s => s.trim());
      return this.evaluateExpression(left) > this.evaluateExpression(right);
    }
    if (condition.includes('<')) {
      const [left, right] = condition.split('<').map(s => s.trim());
      return this.evaluateExpression(left) < this.evaluateExpression(right);
    }

    // Handle boolean values
    if (condition === 'true') return true;
    if (condition === 'false') return false;

    // Treat non-zero numbers as true
    const num = this.evaluateExpression(condition);
    return num !== 0;
  }

  /**
   * Compare values (handles strings and numbers)
   */
  private static compareValues(left: string, right: string): number {
    // Remove quotes if present
    const leftVal = left.replace(/^["']|["']$/g, '');
    const rightVal = right.replace(/^["']|["']$/g, '');

    // Check if both values are pure numbers (not just start with numbers)
    // This regex ensures the entire string is a valid number
    const isNumericString = (str: string) => /^-?\d+(\.\d+)?$/.test(str.trim());

    // Only do numeric comparison if BOTH strings are purely numeric
    if (isNumericString(leftVal) && isNumericString(rightVal)) {
      const leftNum = parseFloat(leftVal);
      const rightNum = parseFloat(rightVal);
      return leftNum === rightNum ? 0 : (leftNum > rightNum ? 1 : -1);
    }

    // String comparison for all other cases (including "1024x1024", "low", etc.)
    return leftVal === rightVal ? 0 : (leftVal > rightVal ? 1 : -1);
  }

  /**
   * Test formula with example parameters (for validation UI)
   */
  static test(formula: string, testCases: FormulaContext[]): {
    valid: boolean;
    results?: number[];
    error?: string;
  } {
    try {
      const results = testCases.map(testCase => this.evaluate(formula, testCase));
      return { valid: true, results };
    } catch (error: any) {
      return { valid: false, error: error.message };
    }
  }
}
